// Pure helpers for the action-modal meeting exports (Copy / Gmail /
// Google Calendar / iCal). Kept separate from React state so they can
// be reused and tested independently.
import { DateTime } from 'luxon';

/**
 * Format the slot time across every city row as a bulleted multi-line
 * string. Used by Copy / Gmail / Google Calendar / iCal exports.
 *
 *   slot     — { dateTime: DateTime }
 *   cityRows — [{ name, timezone, tzAbbr }]
 *
 * Returns the bulleted string with a trailing newline, or '' when slot
 * is missing.
 */
export function formatTimeZoneData(slot, cityRows = []) {
  if (!slot) return '';
  return cityRows.map(city => {
    const t = slot.dateTime.setZone(city.timezone);
    const timeStr = t.toFormat('h:mma');
    const dateStr = t.toFormat('EEE, MMM d yyyy');
    return `• ${city.displayName || city.name} - ${timeStr} (${city.tzAbbr || ''}) - ${dateStr}`;
  }).join('\n') + '\n';
}

/**
 * Build a 1-hour iCalendar event string for a given slot. Caller passes
 * the raw (un-escaped) description; we handle the `\n` → `\\n` escape
 * required by the ICS line format.
 */
export function buildICSForSlot(slot, { summary = "Let's meet", description = '' } = {}) {
  const start = slot.dateTime.toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
  const end = slot.dateTime.plus({ hours: 1 }).toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
  const stamp = DateTime.now().toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
  const uid = `${stamp}-${Math.random().toString(36).slice(2, 11)}@zonewise.app`;
  const escapedDescription = String(description).replace(/\n/g, '\\n');
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//ZoneWise//Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:REQUEST',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${escapedDescription}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

/**
 * Build the Gmail compose URL for a meeting body.
 */
export function buildGmailUrl({ summary = "Let's meet", body = '' } = {}) {
  return `https://mail.google.com/mail/u/0/?fs=1&to=&su=${encodeURIComponent(summary)}&body=${encodeURIComponent(body)}&ui=2&tf=cm`;
}

/**
 * Build the Google Calendar "create event" URL for a 1-hour slot.
 */
export function buildGCalUrlForSlot(slot, { summary = "Let's meet", details = '' } = {}) {
  const start = slot.dateTime.toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
  const end = slot.dateTime.plus({ hours: 1 }).toUTC().toFormat("yyyyMMdd'T'HHmmss'Z'");
  return `https://calendar.google.com/calendar/u/0/r/eventedit?text=${encodeURIComponent(summary)}&dates=${start}/${end}&details=${encodeURIComponent(details)}`;
}

/**
 * Copy text to the clipboard, with a textarea fallback for non-secure
 * contexts (plain-HTTP LAN dev URLs like http://192.168.x.x:3000 where
 * navigator.clipboard is undefined). Returns true on apparent success,
 * false otherwise.
 */
export function writeTextToClipboard(text) {
  if (!text) return false;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {});
    return true;
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
  } catch (err) {
    console.error('Copy fallback failed:', err);
    return false;
  }
}
